import dayjs from "dayjs";
import { CompanyReportMapper } from "@/adapters/CompanyReport.mapper";
import { withCorpGuard } from "@/services/corp/corpGuard";
import { toB2BError, toDataShapeError } from "@/utils/b2bErrors";
import { lazyService } from "@/utils/lazyMock";
import { supabase } from "@/utils/supabase";
/** Границы диапазона дат в МСК (UTC+3) для `charged_at` — копия из `CompanyReportService.ts` (impl-brief). */
const mskBoundary = (date, offsetDays) => `${dayjs(date).add(offsetDays, "day").format("YYYY-MM-DD")}T00:00:00+03:00`;
/**
 * Отчёт по заказам активной компании кабинета (UC1135, контракт v2.0 §4.3 `corp_report_orders`,
 * §4.4 `corp_my_orders`). Реализует общий `ICompanyReportService` — `ReportPanel`/`CompanyReportStore` (UC1124)
 * переиспользуются как есть.
 */
export class CorpReportService {
    async getReport(companyId, params) {
        return withCorpGuard((async () => {
            const { data, error } = await supabase().rpc("corp_report_orders", {
                p_company_id: companyId,
                p_date_from: params.dateFrom,
                p_date_to: params.dateTo,
                p_group_by: params.groupBy,
            });
            if (error)
                throw toB2BError(error);
            try {
                return CompanyReportMapper.reportToDomain(data);
            }
            catch (parseError) {
                throw toDataShapeError(parseError);
            }
        })());
    }
    /** Реестр заказов отчёта — `corp_my_orders` (не `v_corp_orders`), `select("*")`. */
    async listOrders(companyId, dateFrom, dateTo) {
        return withCorpGuard((async () => {
            const { data, error } = await supabase()
                .from("corp_my_orders")
                .select("*")
                .eq("company_id", companyId)
                .eq("is_charged", true)
                .gte("charged_at", mskBoundary(dateFrom, 0))
                .lt("charged_at", mskBoundary(dateTo, 1))
                .order("charged_at");
            if (error)
                throw toB2BError(error);
            try {
                return (data ?? []).map(CompanyReportMapper.orderToDomain);
            }
            catch (parseError) {
                throw toDataShapeError(parseError);
            }
        })());
    }
}
// Литерал REACT_APP_B2B_MOCK — см. CompanyLinkService.ts / utils/lazyMock.ts (G4).
export const corpReportService = process.env.REACT_APP_B2B_MOCK === "true"
    ? lazyService(() => import("@/services/corp/CorpReportServiceMock").then((module) => module.corpReportServiceMock))
    : new CorpReportService();
